import { useState } from 'react';
import { Outlet, useParams, useSearchParams } from 'react-router-dom';

import { RouteErrorBoundary } from '@/shared/components/RouteErrorBoundary';
import { APP_HEADER } from '@/shared/components/layout';

import { usePumsemSearch, usePumsemTree } from '../api/use-pumsem.query';
import { usePumsemAdminOverlay } from '../hooks/usePumsemAdminOverlay';
import { PumsemFacets } from '../components/PumsemFacets';
import { PumsemHeader } from '../components/PumsemHeader';
import { PumsemFooter } from '../components/PumsemFooter';
import { PumsemResultList } from '../components/PumsemResultList';
import { PumsemTreeBrowser } from '../components/PumsemTreeBrowser';
import { PUMSEM_SHELL } from '../components/pumsemShell.constant';
import { PUMSEM_ERROR, codeOf } from '../lib/pumsemError';
import {
  CHANGED_PARAM,
  DIVISION_PARAM,
  EDITION_PARAM,
  QUERY_PARAM,
  RESOURCE_PARAM,
} from '../lib/pumsemNavigation';

/**
 * 품셈해설 셸 — 전용 헤더 / 결과 목록 / 본문.
 *
 * ── 검색 상태가 URL 에 산다 ──────────────────────────────────
 * `?q=`, `?division=`, `?resource=`, `?changed=`, `?edition=` 다섯 개가 전부다.
 * 컴포넌트 state 로 들고 있으면 (1) 결과를 남에게 못 보내고 (2) 상세를 열었다
 * 뒤로 왔을 때 검색이 초기화돼 훑던 자리를 잃는다. 특히 `edition` 은 견적서
 * 근거로 붙일 **판 고정 링크**의 근거라 URL 밖에 있으면 안 된다.
 *
 * 본문 폭을 `max-w-*` 로 묶지 않는다 — 호칭경이 열이라 표가 가로로 넓다.
 * 대신 표 컴포넌트가 자기 안에서 가로 스크롤한다(도움말과 같은 규약).
 *
 * 셸도 **뷰포트 끝까지** 쓴다. 예전엔 헤더가 100rem, 셸이 110rem 이라 넓은
 * 화면에서 로고와 결과 목록이 서로 다른 세로선에 서 있었다. 열 치수는
 * `PUMSEM_SHELL` 한 곳에서 헤더와 함께 본다.
 */
export function PumsemLayoutView() {
  const [params, setParams] = useSearchParams();
  // 레이아웃 라우트에서도 자식 라우트의 파라미터가 잡힌다. 목차가 지금 열려
  // 있는 항목을 표시하고 그 조상을 펼치는 데 쓴다.
  const { division: activeDivision, code: activeCode } = useParams();
  const [listOpen, setListOpen] = useState(false);

  const query = params.get(QUERY_PARAM) ?? '';
  const edition = params.get(EDITION_PARAM);
  const division = params.get(DIVISION_PARAM);
  const resource = params.get(RESOURCE_PARAM);
  const changedOnly = params.get(CHANGED_PARAM) === '1';

  // 관리자면 숨긴 판까지 목록에 실리고, 숨긴 판을 고른 동안에는 검색 출처도
  // 관리 경로로 바뀐다. 공개 API 는 손대지 않는다(훅 주석 참고).
  const { editions, viewingHidden, noPublishedEdition } =
    usePumsemAdminOverlay(edition);
  const {
    data,
    isFetching,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePumsemSearch(
    {
      q: query,
      edition: edition ?? undefined,
      division: division ?? undefined,
      resource: resource ?? undefined,
      changedOnly,
    },
    true,
    viewingHidden,
  );

  const pages = data?.pages ?? [];
  const result = pages[0];
  const hits = pages.flatMap((page) => page.items);
  const searching = query.trim() !== '';

  /**
   * 왼쪽 열의 상주 목차.
   *
   * 예전에는 검색 전 왼쪽이 "검색어를 입력하세요" 한 줄이었다. 그래서 항목
   * 하나를 열고 옆 항목으로 가려면 매번 `/pumsem` 으로 되돌아가 부문·장·절을
   * 처음부터 다시 펼쳐야 했다 — 표준품셈을 보는 실제 동작은 한 절 안의 항목을
   * 위아래로 훑는 것인데, 그 동작에 화면 전환 세 번이 붙어 있었다.
   *
   * 첫 화면(`PumsemIndexView`)과 **같은 쿼리 키**라 왕복이 늘지 않는다.
   * 다만 첫 화면에서는 안 그린다 — 본문이 이미 같은 트리다.
   */
  const { data: tree } = usePumsemTree(edition, viewingHidden);
  const browsing = !searching && !!activeCode;

  /** 한 곳에서만 URL 을 쓴다. 두 군데서 쓰면 조건 하나가 조용히 지워진다. */
  const patch = (next: Record<string, string | null>) => {
    setParams(
      (prev) => {
        const draft = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(next)) {
          if (value === null) draft.delete(key);
          else draft.set(key, value);
        }
        return draft;
      },
      { replace: true },
    );
  };

  const editionLabel =
    editions.find((item) => item.slug === edition)?.label ??
    result?.edition.label ??
    '최신';

  return (
    <div className="flex min-h-screen flex-col">
      <PumsemHeader
        query={query}
        onQueryChange={(value) => patch({ [QUERY_PARAM]: value || null })}
        editions={editions}
        edition={edition}
        onEditionChange={(value) => patch({ [EDITION_PARAM]: value })}
        listOpen={listOpen}
        listLabel={browsing ? '☰ 목차' : '☰ 결과'}
        onToggleList={() => setListOpen((prev) => !prev)}
      />

      {/* 배지만으로는 관리자가 자기가 비공개 판을 보고 있다는 걸 잊는다 —
          "왜 사용자한테 안 보이냐" 는 문의가 여기서 나온다. */}
      {viewingHidden && (
        <div
          className="border-b border-warning/40 bg-warning/10 px-3.5 py-2 text-[13.5px] text-amber-900 dark:text-amber-200"
          role="status"
        >
          {noPublishedEdition ? (
            <>
              공개된 개정판이 아직 없어 <b>숨긴 판</b>을 보고 있습니다 — 일반
              사용자에게는 품셈해설이 비어 있습니다.
            </>
          ) : (
            <>
              <b>{editionLabel}</b> 은(는) 숨긴 판입니다 — 관리자에게만 보이고,
              일반 사용자는 최신 공개판을 봅니다.
            </>
          )}
        </div>
      )}

      {/* 검색 전에는 안 그린다. 이 칩들은 검색 쿼리에만 먹고 아래 훑기 트리는
          안 보므로, 검색어 없이 `최신 개정분만` 을 누르면 켜지기만 하고 아무
          일도 안 일어난다 — 정확히 그걸 해줄 것처럼 생긴 죽은 버튼이었다. */}
      {searching && (
        <div className="border-b border-neutral-200 bg-neutral-50 px-3.5 py-2.5 dark:border-white/12 dark:bg-white/[0.02]">
          <PumsemFacets
            facets={result?.facets ?? []}
            division={division}
            onDivisionChange={(value) => patch({ [DIVISION_PARAM]: value })}
            resource={resource}
            onResourceClear={() => patch({ [RESOURCE_PARAM]: null })}
            changedOnly={changedOnly}
            onChangedOnlyChange={(value) =>
              patch({ [CHANGED_PARAM]: value ? '1' : null })
            }
            editionLabel={editionLabel}
          />
        </div>
      )}

      <div className="w-full flex-1">
        <div className={`grid grid-cols-1 ${PUMSEM_SHELL.grid}`}>
          <aside
            id="pumsem-result-drawer"
            className={`${listOpen ? 'block' : 'hidden'} border-r border-neutral-200 lg:block dark:border-white/12`}
          >
            {/* 높이가 **고정**이어야 한다(`viewportRest` 아님). 목차의 장·절을
                접으면 이 열이 줄고, 본문이 짧은 항목에서는 이 열이 더 큰 쪽이라
                문서 높이까지 같이 줄어 페이지가 위로 튄다. */}
            <div
              className={`sticky ${APP_HEADER.stickyTop} ${APP_HEADER.viewportFill} overflow-y-auto`}
            >
              {/* 라이브 리전은 내용이 바뀌기 **전부터** DOM 에 있어야 읽힌다.
                  등장·소멸하는 리전은 대부분의 스크린리더가 침묵한다. */}
              <div
                className="flex items-center gap-2 px-4 py-3 text-[13.5px] text-neutral-500 dark:text-neutral-400"
                aria-live="polite"
              >
                {!searching &&
                  (browsing ? (
                    <span>목차 · {tree?.edition.label ?? '불러오는 중…'}</span>
                  ) : (
                    <span>검색어를 입력하세요</span>
                  ))}
                {/* 장애를 "0건" 으로 말하면 실무자는 있지도 않은 오타를 찾고,
                    장애는 아무도 신고하지 않는다. */}
                {searching &&
                  isError &&
                  (codeOf(error) === PUMSEM_ERROR.NO_PUBLISHED_EDITION ? (
                    <span className="text-neutral-500">
                      아직 공개된 개정판이 없어 검색할 수 없습니다.
                    </span>
                  ) : (
                    <span role="alert" className="text-danger">
                      결과를 불러오지 못했습니다.{' '}
                      <button
                        type="button"
                        className="underline underline-offset-2"
                        onClick={() => void refetch()}
                      >
                        다시 시도
                      </button>
                    </span>
                  ))}
                {searching && !isError && (
                  <>
                    <b className="text-neutral-900 dark:text-neutral-100">
                      {result?.total ?? 0}건
                    </b>
                    {isFetching && <span>불러오는 중…</span>}
                  </>
                )}
              </div>

              {/* 검색 중이면 결과가, 아니면 목차가 이 열의 주인공이다.
                  둘을 같이 그리면 어느 쪽을 봐야 하는지 알 수 없다. */}
              {searching ? (
                <PumsemResultList
                  hits={hits}
                  buildHref={(itemDivision, code) =>
                    `/pumsem/${itemDivision}/${code}?${params.toString()}`
                  }
                  onResourceClick={(name) =>
                    patch({
                      [RESOURCE_PARAM]: name === resource ? null : name,
                    })
                  }
                  activeResource={resource}
                  // 폰에서는 드로어가 화면을 거의 다 덮는다. 카드를 눌러도 안
                  // 닫으면 상세가 열린 걸 알려면 한 화면을 더 내려야 한다.
                  onNavigate={() => setListOpen(false)}
                />
              ) : (
                browsing &&
                tree && (
                  <PumsemTreeBrowser
                    tree={tree}
                    edition={edition}
                    variant="sidebar"
                    activeDivision={activeDivision}
                    activeCode={activeCode}
                    onNavigate={() => setListOpen(false)}
                  />
                )
              )}

              {searching && hasNextPage && (
                <div className="px-4 py-3">
                  <button
                    type="button"
                    className="w-full rounded-lg border border-neutral-300 py-2 text-[14px] text-neutral-700 transition-colors hover:bg-neutral-50 disabled:opacity-60 dark:border-white/15 dark:text-neutral-200 dark:hover:bg-white/[0.04]"
                    disabled={isFetchingNextPage}
                    onClick={() => void fetchNextPage()}
                  >
                    {isFetchingNextPage
                      ? '불러오는 중…'
                      : `${hits.length}건까지 봤습니다 — 더 보기`}
                  </button>
                </div>
              )}

              {searching && !isError && !isFetching && hits.length === 0 && (
                <p className="px-4 pb-4 text-[14px] leading-relaxed text-neutral-500">
                  <b>{query}</b> 에 해당하는 품셈이 없습니다. 코드(
                  <code>6-2-4</code>)나 직종명(<code>보통인부</code>)으로도 찾을
                  수 있습니다.
                </p>
              )}
            </div>
          </aside>

          <div className="min-w-0">
            {/* 본문 하나가 터져도 헤더·검색·결과 목록은 남아야 한다.
                경계가 없으면 앱 전체가 언마운트돼 흰 화면이 된다. */}
            <RouteErrorBoundary label="품셈해설">
              <Outlet />
            </RouteErrorBoundary>
          </div>
        </div>
      </div>

      <PumsemFooter />
    </div>
  );
}

